import * as React from 'react';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode, REMOVE_LIST_COMMAND, } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { $createQuoteNode, $isQuoteNode, type HeadingTagType, } from '@lexical/rich-text';
import { $isParentElementRTL, $setBlocksType, $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection';
import { $findMatchingParent, $getNearestBlockElementAncestorOrThrow, $getNearestNodeOfType, mergeRegister, } from '@lexical/utils';
import {
	$getSelection,
	$isElementNode,
	$isRangeSelection,
	$isRootOrShadowRoot,
	$isTextNode,
	COMMAND_PRIORITY_CRITICAL,
	COMMAND_PRIORITY_NORMAL,
	type ElementFormatType,
	FORMAT_ELEMENT_COMMAND,
	FORMAT_TEXT_COMMAND,
	INDENT_CONTENT_COMMAND,
	KEY_MODIFIER_COMMAND,
	type LexicalEditor,
	type NodeKey,
	OUTDENT_CONTENT_COMMAND,
	SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { type Dispatch, useCallback, useEffect, useState } from 'react';
import IconFontAwesome from "../../components/iconFontAwesome";
import { IS_APPLE } from '../../helpers/browser';
import { presetTextColors } from "../../page/colors.ts";
import PopoverTextColor from "../../page/components/popoverTextColor.tsx";
import { $createCustomHeadingNode, $isCustomHeadingNode } from "../nodes/customHeadingNode";
import { $isCustomLinkNode } from "../nodes/customLinkNode";
import { $createCustomParagraphNode, $isCustomParagraphNode } from "../nodes/customParagraphNode";
import ColorPicker from "../../components/colorPicker";
import * as styles from '../../components/colorPicker.module.css';
import DropDown, { DropDownItem } from '../ui/DropDown';
import DropdownColorPicker from "../ui/DropdownColorPicker.tsx";
import { getSelectedNode } from '../utils/getSelectedNode';
import { sanitizeUrl } from '../utils/url';

const blockTypeToBlockName = {
	paragraph: 'Normal',
	h1: 'Heading 1',
	h2: 'Heading 2',
	h3: 'Heading 3',
	h4: 'Heading 4',
	h5: 'Heading 5',
	h6: 'Heading 6',
	bullet: 'Bullet list',
	number: 'Ordered list',
	code: 'Code block',
	quote: 'Quote',
	check: 'Check list',
};

const rootTypeToRootName = {
	root: 'Root',
	table: 'Table',
};

// const FONT_FAMILY_OPTIONS: [string, string][] = [
// 	['Arial', 'Arial'],
// 	['Courier New', 'Courier New'],
// 	['Georgia', 'Georgia'],
// 	['Times New Roman', 'Times New Roman'],
// 	['Trebuchet MS', 'Trebuchet MS'],
// 	['Verdana', 'Verdana'],
// ];
//
// const FONT_SIZE_OPTIONS: [string, string][] = [
// 	['10px', '10px'],
// 	['11px', '11px'],
// 	['12px', '12px'],
// 	['13px', '13px'],
// 	['14px', '14px'],
// 	['15px', '15px'],
// 	['16px', '16px'],
// 	['17px', '17px'],
// 	['18px', '18px'],
// 	['19px', '19px'],
// 	['20px', '20px'],
// ];

const ELEMENT_FORMAT_OPTIONS: {[key in Exclude<ElementFormatType, ''>]: {
		icon: string;
		iconRTL: string;
		name: string;
	};} = {
	center: {
		icon: 'icon-align-center',
		iconRTL: 'icon-align-right',
		name: 'Center',
	},
	end: {
		icon: 'icon-align-right',
		iconRTL: 'icon-align-left',
		name: 'End',
	},
	justify: {
		icon: 'icon-align-justify',
		iconRTL: 'icon-align-justify',
		name: 'Justify',
	},
	left: {
		icon: 'icon-align-left',
		iconRTL: 'icon-align-left',
		name: 'Left',
	},
	right: {
		icon: 'icon-align-right',
		iconRTL: 'icon-align-left',
		name: 'Right',
	},
	start: {
		icon: 'icon-align-left',
		iconRTL: 'icon-align-right',
		name: 'Start',
	},
};

function dropDownActiveClass(active: boolean) {
	if (active) return 'active dropdown-item-active';
	else return '';
}

function BlockFormatDropDown({editor, blockType, disabled = false}: {
	blockType: keyof typeof blockTypeToBlockName;
	rootType: keyof typeof rootTypeToRootName;
	editor: LexicalEditor;
	disabled?: boolean;
	}): JSX.Element {
	const formatParagraph = () => {
		editor.update(() => {
			const selection = $getSelection();
			if ($isRangeSelection(selection)) {
				$setBlocksType(selection, () => $createCustomParagraphNode());
			}
		});
	};

	const formatHeading = (headingSize: HeadingTagType) => {
		if (blockType !== headingSize) {
			editor.update(() => {
				const selection = $getSelection();
				$setBlocksType(selection, () => $createCustomHeadingNode(headingSize));
			});
		}
	};

	const formatBulletList = () => {
		if (blockType !== 'bullet') {
			editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
		} else {
			editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
		}
	};

	const formatNumberedList = () => {
		if (blockType !== 'number') {
			editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
		} else {
			editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
		}
	};

	const formatQuote = () => {
		if (blockType !== 'quote') {
			editor.update(() => {
				const selection = $getSelection();
				$setBlocksType(selection, () => $createQuoteNode());
			});
		}
	};

	return (
		<DropDown
			disabled={disabled}
			buttonClassName="toolbar-item block-control spaced"
			buttonIconClassName={'icon block-type ' + blockType}
			buttonLabel={blockTypeToBlockName[blockType]}
			buttonAriaLabel="Formatting options for text style">
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'paragraph')}
				onClick={formatParagraph}>
				<i className="icon paragraph"/>
				<span className="text">Normal</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'h1')}
				onClick={() => formatHeading('h1')}>
				<i className="icon h1"/>
				<span className="text">Heading 1</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'h2')}
				onClick={() => formatHeading('h2')}>
				<i className="icon h2"/>
				<span className="text">Heading 2</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'h3')}
				onClick={() => formatHeading('h3')}>
				<i className="icon h3"/>
				<span className="text">Heading 3</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'h4')}
				onClick={() => formatHeading('h4')}>
				<i className="icon h4"/>
				<span className="text">Heading 4</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'h5')}
				onClick={() => formatHeading('h5')}>
				<i className="icon h5"/>
				<span className="text">Heading 5</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'h6')}
				onClick={() => formatHeading('h6')}>
				<i className="icon h6"/>
				<span className="text">Heading 6</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'bullet')}
				onClick={formatBulletList}>
				<i className="icon bullet-list"/>
				<span className="text">Bullet List</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'number')}
				onClick={formatNumberedList}>
				<i className="icon numbered-list"/>
				<span className="text">Ordered List</span>
			</DropDownItem>
			<DropDownItem
				className={'item ' + dropDownActiveClass(blockType === 'quote')}
				onClick={formatQuote}>
				<i className="icon quote"/>
				<span className="text">Quote</span>
			</DropDownItem>
		</DropDown>
	);
}

function Divider(): JSX.Element {
	return <div className="divider"/>;
}

// function FontDropDown({editor, value, style, disabled = false}: {editor: LexicalEditor; value: string; style: string; disabled?: boolean}): JSX.Element {
// 	const handleClick = useCallback(
// 		(option: string) => {
// 			editor.update(() => {
// 				const selection = $getSelection();
// 				if ($isRangeSelection(selection)) {
// 					$patchStyleText(selection, {[style]: option});
// 				}
// 			});
// 		},
// 		[editor, style],
// 	);
//
// 	const buttonAriaLabel =
// 		style === 'font-family'
// 			? 'Formatting options for font family'
// 			: 'Formatting options for font size';
//
// 	return (
// 		<DropDown
// 			disabled={disabled}
// 			buttonClassName={'toolbar-item ' + style}
// 			buttonLabel={value}
// 			buttonIconClassName={ style === 'font-family' ? 'icon block-type font-family' : '' }
// 			buttonAriaLabel={buttonAriaLabel}>
// 			{(style === 'font-family' ? FONT_FAMILY_OPTIONS : FONT_SIZE_OPTIONS).map(
// 				([option, text]) => (
// 					<DropDownItem
// 						className={`item ${dropDownActiveClass(value === option)} ${ style === 'font-size' ? 'fontsize-item' : '' }`}
// 						onClick={() => handleClick(option)}
// 						key={option}>
// 						<span className="text">{text}</span>
// 					</DropDownItem>
// 				),
// 			)}
// 		</DropDown>
// 	);
// }

function ElementFormatDropdown({editor, value, isRTL, disabled = false}: {
	editor: LexicalEditor;
	value: ElementFormatType;
	isRTL: boolean;
	disabled: boolean;
	}) {
	const formatOption = ELEMENT_FORMAT_OPTIONS[value || 'left'];

	return (
		<DropDown
			disabled={disabled}
			buttonLabel={formatOption.name}
			buttonIconFontAwesomeClassName={`mr-2 ${
				isRTL ? formatOption.iconRTL : formatOption.icon
			}`}
			buttonClassName="toolbar-item spaced alignment"
			buttonAriaLabel="Formatting options for text alignment">
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
				}}
				className="item">
				<IconFontAwesome className="icon-align-left mr-2"/>
				<span className="text">Left</span>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
				}}
				className="item">
				<IconFontAwesome className="icon-align-center mr-2"/>
				<span className="text">Center</span>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
				}}
				className="item">
				<IconFontAwesome className="icon-align-right mr-2"/>
				<span className="text">Right</span>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
				}}
				className="item">
				<IconFontAwesome className="icon-align-justify mr-2"/>
				<span className="text">Justify</span>
			</DropDownItem>
			<Divider/>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
				}}
				className="item">
				<IconFontAwesome className={'mr-2 ' + (isRTL ? 'icon-indent' : 'icon-outdent')}/>
				<span className="text">Outdent</span>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
				}}
				className="item">
				<IconFontAwesome className={'mr-2 ' + (isRTL ? 'icon-outdent' : 'icon-indent')}/>
				<span className="text">Indent</span>
			</DropDownItem>
		</DropDown>
	);
}

export default function ToolbarPlugin({setIsLinkEditMode}: { setIsLinkEditMode: Dispatch<boolean>}): JSX.Element {
	const [editor] = useLexicalComposerContext();
	const [activeEditor, setActiveEditor] = useState(editor);
	const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('paragraph');
	const [rootType, setRootType] = useState<keyof typeof rootTypeToRootName>('root');
	const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
	// const [fontSize, setFontSize] = useState<string>('15px');
	const [fontColor, setFontColor] = useState<string>('#000');
	// const [bgColor, setBgColor] = useState<string>('#fff');
	// const [fontFamily, setFontFamily] = useState<string>('Arial');
	const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');
	const [isLink, setIsLink] = useState(false);
	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);
	const [isUnderline, setIsUnderline] = useState(false);
	const [isStrikethrough, setIsStrikethrough] = useState(false);
	const [isSubscript, setIsSubscript] = useState(false);
	const [isSuperscript, setIsSuperscript] = useState(false);
	const [isRTL, setIsRTL] = useState(false);
	const [isEditable, setIsEditable] = useState(() => editor.isEditable());

	const $updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const anchorNode = selection.anchor.getNode();
			let element =
				anchorNode.getKey() === 'root'
					? anchorNode
					: $findMatchingParent(anchorNode, (e) => {
						const parent = e.getParent();
						return parent !== null && $isRootOrShadowRoot(parent);
					});

			if (element === null) {
				element = anchorNode.getTopLevelElementOrThrow();
			}

			const elementKey = element.getKey();
			const elementDOM = activeEditor.getElementByKey(elementKey);

			// Update text format
			setIsBold(selection.hasFormat('bold'));
			setIsItalic(selection.hasFormat('italic'));
			setIsUnderline(selection.hasFormat('underline'));
			setIsStrikethrough(selection.hasFormat('strikethrough'));
			setIsSubscript(selection.hasFormat('subscript'));
			setIsSuperscript(selection.hasFormat('superscript'));
			setIsRTL($isParentElementRTL(selection));

			// Update links
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isCustomLinkNode(parent) || $isCustomLinkNode(node)) {
				setIsLink(true);
			} else {
				setIsLink(false);
			}

			// const tableNode = $findMatchingParent(node, $isTableNode);
			// if ($isTableNode(tableNode)) {
			// 	setRootType('table');
			// } else {
				setRootType('root');
			// }

			if (elementDOM !== null) {
				setSelectedElementKey(elementKey);
				if ($isListNode(element)) {
					const parentList = $getNearestNodeOfType<ListNode>(
						anchorNode,
						ListNode,
					);
					const type = parentList
						? parentList.getListType()
						: element.getListType();
					setBlockType(type);
				} else {
					let type = $isCustomHeadingNode(element)
						? element.getTag()
						: element.getType();

					if ($isCustomParagraphNode(element)) {
						type = 'paragraph';
					}

					if (type in blockTypeToBlockName) {
						setBlockType(type as keyof typeof blockTypeToBlockName);
					}
				}
			}
			// Handle buttons
			// setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', '15px'));
			setFontColor($getSelectionStyleValueForProperty(selection, 'color', '#000'));
			// setBgColor(
			// 	$getSelectionStyleValueForProperty(
			// 		selection,
			// 		'background-color',
			// 		'#fff',
			// 	),
			// );
			// setFontFamily($getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'));
			setElementFormat(
				($isElementNode(node)
					? node.getFormatType()
					: parent?.getFormatType()) || 'left',
			);
		}
	}, [activeEditor]);

	useEffect(() => {
		return editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			(_payload, newEditor) => {
				$updateToolbar();
				setActiveEditor(newEditor);
				return false;
			},
			COMMAND_PRIORITY_CRITICAL,
		);
	}, [editor, $updateToolbar]);

	useEffect(() => {
		return mergeRegister(
			editor.registerEditableListener((editable) => {
				setIsEditable(editable);
			}),
			activeEditor.registerUpdateListener(({editorState}) => {
				editorState.read(() => {
					$updateToolbar();
				});
			}),
		);
	}, [$updateToolbar, activeEditor, editor]);

	useEffect(() => {
		return activeEditor.registerCommand(
			KEY_MODIFIER_COMMAND,
			(payload) => {
				const event: KeyboardEvent = payload;
				const {code, ctrlKey, metaKey} = event;

				if (code === 'KeyK' && (ctrlKey || metaKey)) {
					event.preventDefault();
					let url: string | null;
					if (!isLink) {
						setIsLinkEditMode(true);
						url = sanitizeUrl('https://');
					} else {
						setIsLinkEditMode(false);
						url = null;
					}
					return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
				}
				return false;
			},
			COMMAND_PRIORITY_NORMAL,
		);
	}, [activeEditor, isLink, setIsLinkEditMode]);


	const onTextColorChange = useCallback((color?: string) => {
		onFontColorSelect(color, true);
	}, []);

	const applyStyleText = useCallback(
		(styles: Record<string, string>, skipHistoryStack?: boolean) => {
			activeEditor.update(
				() => {
					const selection = $getSelection();
					if (selection !== null) {
						$patchStyleText(selection, styles);
					}
				},
				skipHistoryStack ? {tag: 'historic'} : {},
			);
		},
		[activeEditor],
	);

	const clearFormatting = useCallback(() => {
		activeEditor.update(() => {
			const selection = $getSelection();
			if ($isRangeSelection(selection)) { // || $isTableSelection(selection)) {
				const anchor = selection.anchor;
				const focus = selection.focus;
				const nodes = selection.getNodes();
				const extractedNodes = selection.extract();

				if (anchor.key === focus.key && anchor.offset === focus.offset) {
					return;
				}

				nodes.forEach((node, idx) => {
					// We split the first and last node by the selection
					// So that we don't format unselected text inside those nodes
					if ($isTextNode(node)) {
						// Use a separate variable to ensure TS does not lose the refinement
						let textNode = node;
						if (idx === 0 && anchor.offset !== 0) {
							textNode = textNode.splitText(anchor.offset)[1] || textNode;
						}
						if (idx === nodes.length - 1) {
							textNode = textNode.splitText(focus.offset)[0] || textNode;
						}
						/**
						 * If the selected text has one format applied
						 * selecting a portion of the text, could
						 * clear the format to the wrong portion of the text.
						 *
						 * The cleared text is based on the length of the selected text.
						 */
							// We need this in case the selected text only has one format
						const extractedTextNode = extractedNodes[0];
						if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
							textNode = extractedTextNode;
						}

						if (textNode.__style !== '') {
							textNode.setStyle('');
						}
						if (textNode.__format !== 0) {
							textNode.setFormat(0);
							$getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
						}
						node = textNode;
					} else if ($isCustomHeadingNode(node) || $isQuoteNode(node)) {
						node.replace($createCustomParagraphNode(), true);
					} else if ($isDecoratorBlockNode(node)) {
						node.setFormat('');
					}
				});
			}
		});
	}, [activeEditor]);

	const onFontColorSelect = useCallback(
		(value: string, skipHistoryStack: boolean) => {
			// console.log(value, skipHistoryStack);
			applyStyleText({color: value}, skipHistoryStack);
		},
		[applyStyleText],
	);

	const onBgColorSelect = useCallback(
		(value: string, skipHistoryStack: boolean) => {
			applyStyleText({'background-color': value}, skipHistoryStack);
		},
		[applyStyleText],
	);

	const insertLink = useCallback(() => {
		if (!isLink) {
			setIsLinkEditMode(true);
			activeEditor.dispatchCommand(
				TOGGLE_LINK_COMMAND,
				sanitizeUrl('https://'),
			);
		} else {
			setIsLinkEditMode(false);
			activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
		}
	}, [activeEditor, isLink, setIsLinkEditMode]);

	return (
		<div className="toolbar !pr-0 !flex-grow-0">
			{blockType in blockTypeToBlockName && activeEditor === editor && (
				<BlockFormatDropDown
					disabled={!isEditable}
					blockType={blockType}
					rootType={rootType}
					editor={editor}
				/>
			)}
			{/*<FontDropDown*/}
			{/*	disabled={!isEditable}*/}
			{/*	style={'font-family'}*/}
			{/*	value={fontFamily}*/}
			{/*	editor={editor}*/}
			{/*/>*/}
			{/*<FontDropDown*/}
			{/*	disabled={!isEditable}*/}
			{/*	style={'font-size'}*/}
			{/*	value={fontSize}*/}
			{/*	editor={editor}*/}
			{/*/>*/}
			{/*<Divider/>*/}
			<button
				disabled={!isEditable}
				onClick={() => {
					activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
				}}
				className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
				title={IS_APPLE ? 'Bold (⌘B)' : 'Bold (Ctrl+B)'}
				type="button"
				aria-label={`Format text as bold. Shortcut: ${
					IS_APPLE ? '⌘B' : 'Ctrl+B'
				}`}>
				<IconFontAwesome className="icon-bold"/>
			</button>
			<button
				disabled={!isEditable}
				onClick={() => {
					activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
				}}
				className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
				title={IS_APPLE ? 'Italic (⌘I)' : 'Italic (Ctrl+I)'}
				type="button"
				aria-label={`Format text as italics. Shortcut: ${
					IS_APPLE ? '⌘I' : 'Ctrl+I'
				}`}>
				<IconFontAwesome className="icon-italic"/>
			</button>
			<button
				disabled={!isEditable}
				onClick={insertLink}
				className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
				aria-label="Insert link"
				title="Insert link"
				type="button">
				<IconFontAwesome className="icon-link"/>
			</button>
			<PopoverTextColor
				buttonCssClass="mx-1"
				color={fontColor}
				onChange={onTextColorChange}
				disabled={!isEditable}
			/>
			{/*<DropDown*/}
			{/*	disabled={!isEditable}*/}
			{/*	stopCloseOnClickSelf={false}*/}
			{/*	buttonClassName="">*/}
			{/*	<ColorPicker*/}
			{/*		color={fontColor}*/}
			{/*		onChange={onTextColorChange}*/}
			{/*		preset={presetTextColors}*/}
			{/*	/>*/}
			{/*</DropDown>*/}
			{/*<DropdownColorPicker*/}
			{/*	disabled={!isEditable}*/}
			{/*	buttonClassName="toolbar-item color-picker"*/}
			{/*	buttonAriaLabel="Formatting background color"*/}
			{/*	buttonIconClassName="icon bg-color"*/}
			{/*	color={bgColor}*/}
			{/*	onChange={onBgColorSelect}*/}
			{/*	title="bg color"*/}
			{/*/>*/}

			<DropDown
				disabled={!isEditable}
				buttonClassName="toolbar-item spaced"
				buttonLabel=""
				buttonAriaLabel="Formatting options for additional text styles"
				buttonIconClassName="icon dropdown-more">
				<DropDownItem
					onClick={() => {
						activeEditor.dispatchCommand(
							FORMAT_TEXT_COMMAND,
							'strikethrough',
						);
					}}
					className={'item ' + dropDownActiveClass(isStrikethrough)}
					title="Strikethrough"
					aria-label="Format text with a strikethrough">
					<IconFontAwesome className="icon-strikethrough mr-2"/>
					<span className="text">Strikethrough</span>
				</DropDownItem>

				<DropDownItem
					onClick={() => {
						activeEditor.dispatchCommand(
							FORMAT_TEXT_COMMAND,
							'underline',
						);
					}}
					className={'item ' + dropDownActiveClass(isUnderline)}
					title={IS_APPLE ? 'Underline (⌘U)' : 'Underline (Ctrl+U)'}
					aria-label={`Format text to underlined. Shortcut: ${
						IS_APPLE ? '⌘U' : 'Ctrl+U'
					}`}>
					<IconFontAwesome className="icon-underline mr-2"/>
					<span className="text">Underline</span>
				</DropDownItem>

				<DropDownItem
					onClick={() => {
						activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
					}}
					className={'item ' + dropDownActiveClass(isSubscript)}
					title="Subscript"
					aria-label="Format text with a subscript">
					<IconFontAwesome className="icon-subscript mr-2"/>
					<span className="text">Subscript</span>
				</DropDownItem>
				<DropDownItem
					onClick={() => {
						activeEditor.dispatchCommand(
							FORMAT_TEXT_COMMAND,
							'superscript',
						);
					}}
					className={'item ' + dropDownActiveClass(isSuperscript)}
					title="Superscript"
					aria-label="Format text with a superscript">
					<IconFontAwesome className="icon-superscript mr-2"/>
					<span className="text">Superscript</span>
				</DropDownItem>
				<DropDownItem
					onClick={clearFormatting}
					className="item"
					title="Clear text formatting"
					aria-label="Clear all text formatting">
					<IconFontAwesome className="icon-trash mr-2"/>
					<span className="text">Clear Formatting</span>
				</DropDownItem>
			</DropDown>

			<Divider/>

			<ElementFormatDropdown
				disabled={!isEditable}
				value={elementFormat}
				editor={editor}
				isRTL={isRTL}
			/>
		</div>
	);
}
